import { IScope } from "@/api";
import { ActionButton } from "@/components/shared/buttons/ActionButton";
import { Tooltip } from "@/components/shared/Tooltip";
import { FindInPageOutlined } from "@mui/icons-material";
import { ButtonProps, SxProps, Theme, TooltipProps } from "@mui/material";
import { useMemo, useState } from "react";
import { CreateChartModal } from "./CreateChartModal";
import { MAX_SCOPES } from "./ScopeSelectionList";

interface CompareInChartButtonProps {
    selectedScopeIds: number[];
    rows: Pick<IScope, "id" | "clause_checklist_id" | "document_title">[];
    clauseChecklistName: string;
    tooltipPlacement?: TooltipProps["placement"];
    size?: ButtonProps["size"];
    variant?: ButtonProps["variant"];
    sx?: SxProps<Theme>;
}

const DEFAULT_TOOLTIP = "Select 2 or more scopes to compare";

export const CompareInChartButton = ({
    selectedScopeIds,
    rows,
    clauseChecklistName,
    tooltipPlacement = "bottom",
    size,
    variant,
    sx: sxProp,
}: CompareInChartButtonProps) => {
    const [modalOpen, setModalOpen] = useState(false);

    const selectedRows = useMemo(
        () => rows.filter((row) => selectedScopeIds.includes(row.id)),
        [rows, selectedScopeIds]
    );

    const sameClauseChecklist =
        selectedRows.length > 0 &&
        selectedRows.every(
            (row) =>
                row.clause_checklist_id === selectedRows[0].clause_checklist_id
        );

    const overLimit = selectedScopeIds.length > MAX_SCOPES;
    const canCompare =
        selectedScopeIds.length >= 2 && sameClauseChecklist && !overLimit;

    const tooltip = useMemo(() => {
        if (overLimit) {
            return {
                title: `Select at most ${MAX_SCOPES} scopes to compare`,
                forceOpen: true,
            };
        }
        if (selectedRows.length > 1 && !sameClauseChecklist) {
            return {
                title: "Selected scopes must have the same clause checklist",
                forceOpen: true,
            };
        }
        if (selectedRows.length === 1) {
            return { title: DEFAULT_TOOLTIP, forceOpen: true };
        }
        return { title: DEFAULT_TOOLTIP, forceOpen: false };
    }, [selectedRows.length, sameClauseChecklist, overLimit]);

    const [tooltipOpen, setTooltipOpen] = useState(false);

    return (
        <>
            <Tooltip
                title={tooltip.title}
                placement={tooltipPlacement}
                open={tooltip.forceOpen || tooltipOpen}
                onClose={() => setTooltipOpen(false)}
                onOpen={() => setTooltipOpen(true)}
                arrow
                slotProps={{
                    popper: { style: { zIndex: 1200 } },
                }}
            >
                <span>
                    <ActionButton
                        icon={<FindInPageOutlined />}
                        disabled={!canCompare}
                        onClick={() => setModalOpen(true)}
                        {...(size && { size })}
                        {...(variant && { variant })}
                        {...(sxProp && { sx: sxProp })}
                    >
                        Compare
                    </ActionButton>
                </span>
            </Tooltip>
            {modalOpen && canCompare && (
                <CreateChartModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    initialScopes={selectedRows}
                    clauseChecklistId={selectedRows[0].clause_checklist_id}
                    clauseChecklistName={clauseChecklistName}
                />
            )}
        </>
    );
};
